/**
 * Hook para gerenciamento de transferência em massa
 * 
 * RELEASE: 1.0.0 - 2025-01-22
 * 
 * Funcionalidades:
 * - Preview: Retorna contagem de registros afetados antes da execução
 * - Execute: Executa transferência atômica via RPC
 * - Histórico: Busca logs de transferências anteriores
 * 
 * Tipos de transferência suportados:
 * - conversations: Transferir agente responsável das conversas
 * - deals_assigned: Transferir responsável interno do negócio
 * - deals_seller: Transferir vendedor comercial do negócio
 * - contacts_seller: Transferir vendedor comercial do contato
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger para debug
const logger = createLogger('useBulkTransfer');

// =============================================================================
// TIPOS E INTERFACES
// =============================================================================

/** Tipos de transferência disponíveis */
/** v2.5.0: Tipos de transferência disponíveis (inclui conversations_department) */
export type TransferType = 'conversations' | 'deals_assigned' | 'deals_seller' | 'contacts_seller' | 'conversations_department';

/** v2.5.0: Limite máximo de itens por execução de transferência em massa */
export const MAX_BULK_TRANSFER_ITEMS = 200;

/** Filtros para transferência em massa */
export interface TransferFilters {
  department_id?: string;
  inbox_ids?: string[];        // v2.4.0: Filtro de inboxes (seleção múltipla)
  queue_situations?: string[]; // v2.4.0: Filtro de fase kanban (seleção múltipla)
  workspace_id?: string;       // Filtro de workspace para negócios
  pipeline_id?: string;
  phase_ids?: string[];        // Múltiplas fases (seleção múltipla)
  status?: string;
  date_from?: string;
  date_to?: string;
  transfer_limit?: number;     // v2.5.0: Limite de itens por execução (max 200)
}

/** Modo de destino da transferência (v2.3.0) */
export type TransferTargetMode = 'direct' | 'queue';

/** Constante para indicar origem sem atribuição (v2.3.0) */
export const UNASSIGNED_SOURCE = '__UNASSIGNED__';

/** Resultado do preview de transferência */
export interface TransferPreviewResult {
  count: number;
  pending_transfers: number;
  transfer_type: TransferType;
  from_id: string;
  is_unassigned?: boolean;      // v2.3.0: Flag para indicar origem sem atribuição
  transfer_limit?: number;      // v2.5.0: Limite aplicado
  will_be_limited?: boolean;    // v2.5.0: Se a contagem excede o limite
}

/** Resultado da execução de transferência */
export interface TransferExecutionResult {
  success: boolean;
  log_id: string;
  transferred: number;
  skipped: number;
  cancelled_transfers?: number;
  redirected_transfers?: number;       // v2.2.4: Transferências redirecionadas
  distributed_via_queue?: number;      // v2.3.0: Quantidade distribuída via fila
  target_mode?: TransferTargetMode;    // v2.3.0: Modo de destino usado
  is_unassigned_source?: boolean;      // v2.3.0: Se origem era "sem atribuição"
}

/** Log de transferência em massa */
export interface BulkTransferLog {
  id: string;
  tenant_id: string;
  executed_by: string;
  transfer_type: TransferType;
  from_agent_id: string | null;
  to_agent_id: string | null;
  from_seller_id: string | null;
  to_seller_id: string | null;
  filters: TransferFilters;
  total_requested: number;
  total_transferred: number;
  total_skipped: number;
  total_errors: number;
  affected_ids: string[];
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

/** Estado do wizard de transferência */
export interface TransferWizardState {
  step: number;
  transferType: TransferType | null;
  fromId: string | null;
  toId: string | null;
  targetMode: TransferTargetMode;       // v2.3.0: Modo de destino (direct ou queue)
  queueId: string | null;               // v2.3.0: ID da fila (quando targetMode = 'queue')
  filters: TransferFilters;
  previewResult: TransferPreviewResult | null;
  executionResult: TransferExecutionResult | null;
  isLoading: boolean;
  error: string | null;
}

// =============================================================================
// LABELS E DESCRIÇÕES
// =============================================================================

/** Labels amigáveis para tipos de transferência */
export const TRANSFER_TYPE_LABELS: Record<TransferType, string> = {
  conversations: 'Conversas',
  deals_assigned: 'Negócios (Responsável)',
  deals_seller: 'Negócios (Vendedor)',
  contacts_seller: 'Contatos (Vendedor)',
  conversations_department: 'Departamento (Conversa)', // v2.5.0
};

/** Descrições para tipos de transferência */
export const TRANSFER_TYPE_DESCRIPTIONS: Record<TransferType, string> = {
  conversations: 'Transferir o agente responsável das conversas abertas',
  deals_assigned: 'Transferir o responsável interno dos negócios em andamento',
  deals_seller: 'Transferir o vendedor comercial vinculado aos negócios',
  contacts_seller: 'Transferir o vendedor comercial vinculado aos contatos',
  conversations_department: 'Transferir conversas de um departamento para outro', // v2.5.0
};

// =============================================================================
// HOOK PRINCIPAL
// =============================================================================

/**
 * Opções do hook useBulkTransfer
 * - enabledLogs: quando true, busca o histórico de transferências via RPC.
 *   Default false para evitar SELECT desnecessário ao montar o dialog.
 */
export interface UseBulkTransferOptions {
  enabledLogs?: boolean;
}

export function useBulkTransfer(options: UseBulkTransferOptions = {}) {
  const { enabledLogs = false } = options;
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  
  // Estado do wizard
  const [wizardState, setWizardState] = useState<TransferWizardState>({
    step: 1,
    transferType: null,
    fromId: null,
    toId: null,
    targetMode: 'direct',  // v2.3.0: Modo padrão é direto
    queueId: null,         // v2.3.0: Sem fila por padrão
    filters: {},
    previewResult: null,
    executionResult: null,
    isLoading: false,
    error: null,
  });

  // =========================================================================
  // FUNÇÕES DO WIZARD
  // =========================================================================

  /** Avançar para próximo step */
  const nextStep = useCallback(() => {
    setWizardState(prev => ({ ...prev, step: prev.step + 1, error: null }));
  }, []);

  /** Voltar para step anterior */
  const prevStep = useCallback(() => {
    setWizardState(prev => ({ ...prev, step: Math.max(1, prev.step - 1), error: null }));
  }, []);

  /** Ir para step específico */
  const goToStep = useCallback((step: number) => {
    setWizardState(prev => ({ ...prev, step, error: null }));
  }, []);

  /** Definir tipo de transferência */
  const setTransferType = useCallback((type: TransferType) => {
    setWizardState(prev => ({ 
      ...prev, 
      transferType: type,
      // Resetar origem/destino quando muda o tipo
      fromId: null,
      toId: null,
      targetMode: 'direct',  // v2.3.0: Resetar modo ao mudar tipo
      queueId: null,         // v2.3.0: Resetar fila ao mudar tipo
      previewResult: null,
      error: null,
    }));
  }, []);

  /** Definir origem da transferência */
  const setFromId = useCallback((id: string) => {
    setWizardState(prev => ({ 
      ...prev, 
      fromId: id, 
      previewResult: null,
      error: null,
    }));
  }, []);

  /** Definir destino da transferência */
  const setToId = useCallback((id: string) => {
    setWizardState(prev => ({ 
      ...prev, 
      toId: id,
      error: null,
    }));
  }, []);

  /** Atualizar filtros */
  const setFilters = useCallback((filters: Partial<TransferFilters>) => {
    setWizardState(prev => ({ 
      ...prev, 
      filters: { ...prev.filters, ...filters },
      previewResult: null,
      error: null,
    }));
  }, []);

  /** v2.3.0: Definir modo de destino (direct ou queue) */
  const setTargetMode = useCallback((mode: TransferTargetMode) => {
    setWizardState(prev => ({ 
      ...prev, 
      targetMode: mode,
      // Se mudar para direct, limpar queueId; se mudar para queue, limpar toId
      toId: mode === 'queue' ? null : prev.toId,
      queueId: mode === 'direct' ? null : prev.queueId,
      error: null,
    }));
  }, []);

  /** v2.3.0: Definir fila de distribuição */
  const setQueueId = useCallback((id: string | null) => {
    setWizardState(prev => ({ 
      ...prev, 
      queueId: id,
      error: null,
    }));
  }, []);

  /** Resetar wizard para estado inicial */
  const resetWizard = useCallback(() => {
    setWizardState({
      step: 1,
      transferType: null,
      fromId: null,
      toId: null,
      targetMode: 'direct',  // v2.3.0
      queueId: null,         // v2.3.0
      filters: {},
      previewResult: null,
      executionResult: null,
      isLoading: false,
      error: null,
    });
  }, []);

  // =========================================================================
  // MUTATION: PREVIEW
  // =========================================================================

  const previewMutation = useMutation({
    mutationFn: async (params: { 
      transferType: TransferType; 
      fromId: string; 
      filters?: TransferFilters;
    }) => {
      logger.info('Executando preview de transferência', params);
      
      // GUARD DE SEGURANÇA: Frontend NUNCA deve passar tenant_hint
      // Apenas Edge Functions com service_role podem usar p_tenant_hint
      // A RPC usa get_validated_tenant_id() internamente para obter o tenant do usuário autenticado
      
      const { data, error } = await (supabase.rpc as any)('bulk_transfer_preview', {
        p_transfer_type: params.transferType,
        p_from_id: params.fromId,
        p_filters: JSON.parse(JSON.stringify(params.filters || {})),
        // SEGURANÇA: Não passar p_tenant_hint - a RPC usa get_validated_tenant_id() internamente
      });

      if (error) {
        logger.error('Erro no preview', { error: error.message });
        throw new Error(error.message);
      }

      return data as unknown as TransferPreviewResult;
    },
    onSuccess: (result) => {
      logger.info('Preview concluído', result);
      setWizardState(prev => ({ 
        ...prev, 
        previewResult: result,
        isLoading: false,
        error: null,
      }));
    },
    onError: (error: Error) => {
      setWizardState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: error.message,
      }));
      toast.error('Erro ao obter preview', { description: error.message });
    },
  });

  // =========================================================================
  // MUTATION: EXECUÇÃO
  // =========================================================================

  const executeMutation = useMutation({
    mutationFn: async (params: { 
      transferType: TransferType; 
      fromId: string; 
      toId: string | null;       // v2.3.0: Pode ser null em modo queue
      targetMode: TransferTargetMode;  // v2.3.0: Modo de destino
      queueId: string | null;    // v2.3.0: ID da fila
      filters?: TransferFilters;
    }) => {
      logger.info('Executando transferência em massa', params);
      
      // GUARD DE SEGURANÇA: Frontend NUNCA deve passar tenant_hint
      // Apenas Edge Functions com service_role podem usar p_tenant_hint
      // A RPC usa get_validated_tenant_id() internamente para obter o tenant do usuário autenticado
      
      const { data, error } = await (supabase.rpc as any)('bulk_transfer_ownership', {
        p_transfer_type: params.transferType,
        p_from_id: params.fromId,
        p_to_id: params.toId,  // v2.3.0: Pode ser null em modo queue
        p_filters: JSON.parse(JSON.stringify(params.filters || {})),
        p_target_mode: params.targetMode,  // v2.3.0: Novo parâmetro
        p_queue_id: params.queueId,        // v2.3.0: Novo parâmetro
        // SEGURANÇA: Não passar p_tenant_hint - a RPC usa get_validated_tenant_id() internamente
      });

      if (error) {
        logger.error('Erro na transferência', { error: error.message });
        throw new Error(error.message);
      }

      return data as unknown as TransferExecutionResult;
    },
    onSuccess: (result) => {
      logger.info('Transferência concluída', result);
      setWizardState(prev => ({ 
        ...prev, 
        executionResult: result,
        isLoading: false,
        error: null,
      }));
      
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['bulk-transfer-logs'] });
      
      // v2.3.0: Mensagem de sucesso diferenciada por modo
      const isQueueMode = result.target_mode === 'queue';
      toast.success(isQueueMode ? 'Distribuição concluída!' : 'Transferência concluída!', {
        description: isQueueMode 
          ? `${result.distributed_via_queue || result.transferred} registros distribuídos via fila.`
          : `${result.transferred} registros transferidos com sucesso.`,
      });
    },
    onError: (error: Error) => {
      setWizardState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: error.message,
      }));
      toast.error('Erro na transferência', { description: error.message });
    },
  });

  // =========================================================================
  // FUNÇÕES PÚBLICAS
  // =========================================================================

  /** Executar preview com estado atual do wizard */
  const executePreview = useCallback(async () => {
    if (!wizardState.transferType || !wizardState.fromId) {
      setWizardState(prev => ({ 
        ...prev, 
        error: 'Selecione o tipo de transferência e a origem',
      }));
      return;
    }

    setWizardState(prev => ({ ...prev, isLoading: true, error: null }));
    
    await previewMutation.mutateAsync({
      transferType: wizardState.transferType,
      fromId: wizardState.fromId,
      filters: wizardState.filters,
    });
  }, [wizardState.transferType, wizardState.fromId, wizardState.filters, previewMutation]);

  /** Executar transferência com estado atual do wizard */
  const executeTransfer = useCallback(async () => {
    // v2.3.0: Validação depende do modo
    const isQueueMode = wizardState.targetMode === 'queue';
    
    if (!wizardState.transferType || !wizardState.fromId) {
      setWizardState(prev => ({ 
        ...prev, 
        error: 'Dados incompletos para transferência',
      }));
      return;
    }
    
    // Validação específica por modo
    if (isQueueMode && !wizardState.queueId) {
      setWizardState(prev => ({ 
        ...prev, 
        error: 'Selecione uma fila de distribuição',
      }));
      return;
    }
    
    if (!isQueueMode && !wizardState.toId) {
      setWizardState(prev => ({ 
        ...prev, 
        error: 'Selecione o destino da transferência',
      }));
      return;
    }

    setWizardState(prev => ({ ...prev, isLoading: true, error: null }));
    
    await executeMutation.mutateAsync({
      transferType: wizardState.transferType,
      fromId: wizardState.fromId,
      toId: wizardState.toId,
      targetMode: wizardState.targetMode,
      queueId: wizardState.queueId,
      filters: wizardState.filters,
    });
  }, [
    wizardState.transferType, 
    wizardState.fromId, 
    wizardState.toId, 
    wizardState.targetMode, 
    wizardState.queueId, 
    wizardState.filters, 
    executeMutation
  ]);

  // =========================================================================
  // QUERY: HISTÓRICO DE TRANSFERÊNCIAS
  // =========================================================================

  const { data: transferLogs, isLoading: isLoadingLogs } = useQuery({
    queryKey: ['bulk-transfer-logs', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      // Busca server-side via RPC (substitui SELECT direto na tabela)
      const { data, error } = await supabase.rpc('list_bulk_transfer_logs', {
        p_limit: 50,
      });

      if (error) throw error;
      return (data || []) as unknown as BulkTransferLog[];
    },
    // Default: desabilitado. Só roda quando explicitamente solicitado pelo consumidor
    enabled: !!currentTenant?.id && enabledLogs,
  });

  // =========================================================================
  // RETORNO DO HOOK
  // =========================================================================

  return {
    // Estado do wizard
    wizardState,
    
    // Funções de navegação
    nextStep,
    prevStep,
    goToStep,
    resetWizard,
    
    // Funções de configuração
    setTransferType,
    setFromId,
    setToId,
    setTargetMode,   // v2.3.0
    setQueueId,      // v2.3.0
    setFilters,
    
    // Funções de execução
    executePreview,
    executeTransfer,
    
    // Estados das mutations
    isPreviewLoading: previewMutation.isPending,
    isExecuteLoading: executeMutation.isPending,
    
    // Histórico
    transferLogs,
    isLoadingLogs,
  };
}
